import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface OutstandingDebtResult {
  hasDebt: boolean;
  totalOwed: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useOutstandingDebt(userId: string | undefined): OutstandingDebtResult {
  const [hasDebt, setHasDebt] = useState(false);
  const [totalOwed, setTotalOwed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (!userId) {
      setHasDebt(false);
      setTotalOwed(0);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDebt = async () => {
      setLoading(true);
      setError(null);
      try {
        const q = query(
          collection(db, 'settlement_transactions'),
          where('from_user_id', '==', userId),
          where('status', '==', 'pending')
        );
        const snap = await getDocs(q);
        const total = snap.docs.reduce((sum, doc) => sum + (doc.data().amount ?? 0), 0);
        if (!cancelled) {
          setHasDebt(total > 0);
          setTotalOwed(total);
          setLoading(false);
        }
      } catch (err) {
        console.error('useOutstandingDebt: failed to query', err);
        if (!cancelled) {
          setHasDebt(false);
          setTotalOwed(0);
          setError('Failed to check outstanding debt');
          setLoading(false);
        }
      }
    };

    fetchDebt();
    return () => { cancelled = true; };
  }, [userId, refreshKey]);

  return { hasDebt, totalOwed, loading, error, refresh };
}
